import { $CombinedState, combineReducers, Reducer } from '@reduxjs/toolkit';
import { persistReducer, Storage } from 'redux-persist';
import storage from 'redux-persist/lib/storage'; // defaults to localStorage for web

import APIAuthReducer from './api-auth/reducer';
import UserInfoReducer from './user-info/reducer';
import UsersListReducer from './users/reducer';
import UserReducer from './user/reducer';
import UserInfoToModifyReducer from './user-info-to-modify/reducer';
import resetPassword from './reset-password/reducer';
import CirculationsReducer from './circulations/reducer';
import stationsReducer from './stations/reducer';
import StationsServedReducer from './stations-served/reducer';
import ReasonsReducer from './reasons/reducer';
import ProductCodesReducer from './product-codes/reducer';
import ProductCodeReducer from './product-code/reducer';
import HelpInfosReducer from './help-infos/reducer';
import HelpInfoReducer from './help-info/reducer';
import ReasonReducer from './reason/reducer';
import CustomersListReducer from './customers-list/reducer';
// Communication ------------------------------------------------------------------------------------------
import StationsTemplatesPreviewReducer from './stations-templates-preview/reducer';
import HistoricCommunicationReducer from './historic-communication/reducer';
import HistoricPendingCommunicationReducer from './historic-pending-communication/reducer';
import ServicesPassengersConcernedByCommunicationReducer from './communication-impacted-passengers/reducer';
import LatestCommunicationReducer from './latest-communication/reducer';
import SendCommunicationReducer from './send-communication/reducer';
// --------------------------------------------------------------------------------------------------------
import ApiVersionReducer from './api-version/reducer';
import SituationsReducer from './situations/reducer';
import SituationsCommunicationsReducer from './situations-communications/reducer';
import SituationsCategoryReducer from './situations-category/reducer';
import SituationCategoryReducer from './situation-category/reducer';
import SituationReducer from './situation/reducer';
import MasksReducer from './masks/reducer';
import MaskReducer from './mask/reducer';
import MasksConfigReducer from './masks-config/reducer';
import ProfilesListReducer from './profiles/reducer';
import GroupsListReducer from './groups/reducer';
import OtherSelectionReducer from './other-selection/reducer';
import TypologyReducer from './typology/reducer';
import CompensationSentencesReducer from './compensation-sentences/reducer';
import CompensationSentenceReducer from './compensation-sentence/reducer';
import MessagesMasksReducer from './messages-mask/reducer';
import ProfilReducer from './profil/reducer';
import SectionsReducer from './sections/reducer';
import ServiceTypesReducer from './service-types/reducer';
import TranslationLanguagesReducer from './translation-languages/reducer';

export type ReducerKeys =
  | 'APIAuth'
  | 'ApiVersion'
  | 'Circulations'
  | 'ServicesPassengersConcernedByCommunication'
  | 'CompensationSentence'
  | 'CompensationSentences'
  | 'Profil'
  | 'User'
  | 'CustomersList'
  | 'GroupsList'
  | 'HelpInfo'
  | 'HelpInfos'
  | 'HistoricCommunication'
  | 'HistoricPendingCommunication'
  | 'LatestCommunication'
  | 'Mask'
  | 'Masks'
  | 'MasksConfig'
  | 'MessagesMasks'
  | 'ProductCode'
  | 'ProductCodes'
  | 'ProfilesList'
  | 'Reason'
  | 'Reasons'
  | 'ResetPassword'
  | 'Sections'
  | 'SendCommunication'
  | 'Situation'
  | 'SituationCategory'
  | 'Situations'
  | 'SituationsCommunications'
  | 'SituationsCategory'
  | 'stations'
  | 'StationsServed'
  | 'StationsTemplatesPreview'
  | 'OtherSelection'
  | 'Typology'
  | 'UserInfo'
  | 'UserInfoToModify'
  | 'UsersList'
  | 'ServiceTypes'
  | 'TranslationLanguages';

// avoid persisting status, because of the button infinite loading issue which append when we persist status loading and then refresh the page (button is stuck/disabled because of status loading persisted)
// avoid persisting error, because of the error message which append when we persist status loading and then refresh the page (error message is stuck/disabled because of error persisted)
const defaultPersistConfig = {
  storage: storage as Storage,
  blacklist: ['status', 'error', 'message'],
};

/**
 * please persist slices with caution (don't do it if you don't have to),
 * typically, you should persist slices which should be available between browser tabs, or after a refresh (like SendCommunication, UserInfo, etc.)
 */
const reducers: { [key in ReducerKeys]: Reducer } = {
  APIAuth: persistReducer(
    {
      ...defaultPersistConfig,
      blacklist: [...defaultPersistConfig.blacklist, 'isLoginExpired'],
      key: 'APIAuth',
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    APIAuthReducer as any
  ) as unknown as typeof APIAuthReducer,
  ApiVersion: ApiVersionReducer,
  Circulations: persistReducer(
    {
      ...defaultPersistConfig,
      blacklist: [...defaultPersistConfig.blacklist, 'data', 'refreshedDate'],
      key: 'Circulations',
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    CirculationsReducer as any
  ) as unknown as typeof CirculationsReducer,
  ServicesPassengersConcernedByCommunication: ServicesPassengersConcernedByCommunicationReducer,
  CompensationSentence: CompensationSentenceReducer,
  CompensationSentences: CompensationSentencesReducer,
  Profil: ProfilReducer,
  User: UserReducer,
  CustomersList: CustomersListReducer,
  GroupsList: GroupsListReducer,
  HelpInfo: HelpInfoReducer,
  HelpInfos: HelpInfosReducer,
  HistoricCommunication: HistoricCommunicationReducer,
  HistoricPendingCommunication: HistoricPendingCommunicationReducer,
  LatestCommunication: LatestCommunicationReducer,
  Mask: MaskReducer,
  Masks: MasksReducer,
  MasksConfig: MasksConfigReducer,
  MessagesMasks: MessagesMasksReducer,
  ProductCode: ProductCodeReducer,
  ProductCodes: ProductCodesReducer,
  ProfilesList: ProfilesListReducer,
  Reason: ReasonReducer,
  Reasons: ReasonsReducer,
  ResetPassword: resetPassword,
  Sections: SectionsReducer,
  SendCommunication: persistReducer(
    {
      ...defaultPersistConfig,
      blacklist: [
        ...defaultPersistConfig.blacklist,
        'initializeCommunication.status',
        'initializeCommunication.error',
        'saveCommunication',
        'firstStepformValues',
        'secondStepformValues',
      ],
      key: 'SendCommunication',
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    SendCommunicationReducer as any
  ) as unknown as typeof SendCommunicationReducer,
  Situation: SituationReducer,
  SituationCategory: SituationCategoryReducer,
  Situations: SituationsReducer,
  SituationsCommunications: SituationsCommunicationsReducer,
  SituationsCategory: SituationsCategoryReducer,
  stations: stationsReducer,
  StationsServed: StationsServedReducer,
  StationsTemplatesPreview: StationsTemplatesPreviewReducer,
  OtherSelection: OtherSelectionReducer,
  Typology: TypologyReducer,
  UserInfo: persistReducer(
    { ...defaultPersistConfig, key: 'UserInfo' },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    UserInfoReducer as any
  ) as unknown as typeof UserInfoReducer,
  UserInfoToModify: UserInfoToModifyReducer,
  UsersList: UsersListReducer,
  ServiceTypes: ServiceTypesReducer,
  TranslationLanguages: TranslationLanguagesReducer,
};

const rootReducer = combineReducers(reducers);

// A typescript fix for all selectors files (exple error: Exported variable 'errorSelector' has or is using name '$CombinedState' from external module)
// https://stackoverflow.com/questions/67293951/exported-variable-store-has-or-is-using-name-combinedstate-from-external-mo
export type RootState = ReturnType<typeof rootReducer> & {
  readonly [$CombinedState]?: undefined;
};

export default rootReducer;
